import type { GeoPoint } from '@@partner:types:app'

import { isGeoPointValid } from '@@partner:helpers/util'
import MapMarker from '@components/common/MapMarker'
import {
  LatLngLiteral,
  Marker as LeafletEditableMarker,
  Map as LeafletMap
} from 'leaflet'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet'

type OrderMapMarkerProps = {
  geoPoint?: GeoPoint
  onPositionChanged: (point: GeoPoint) => void
}

export const DEFAULT_MAP_CENTER: GeoPoint = {
  latitude: 43.238949,
  longitude: 76.889709
}

const convertGeoPointToLeafletLatLng = (point: GeoPoint): LatLngLiteral => ({
  lat: point.latitude as number,
  lng: point.longitude as number
})

const convertLeafletLatLngToGeoPoint = (
  latLngExpr: LatLngLiteral
): GeoPoint => ({
  latitude: latLngExpr.lat,
  longitude: latLngExpr.lng
})

function MapController ({ geoPoint }: { geoPoint?: GeoPoint }) {
  const map = useMap()

  useEffect(() => {
    if (geoPoint && geoPoint.latitude && geoPoint.longitude) {
      const newPosition = convertGeoPointToLeafletLatLng(geoPoint)
      map.setView(newPosition, 13)
    }
  }, [ geoPoint, map ])

  return null
}

export default function OrderMapMarker ({
  geoPoint,
  onPositionChanged
}: OrderMapMarkerProps) {
  const markerRef = useRef<LeafletEditableMarker>(null)
  const [ markerPosition, setMarkerPosition ] = useState(
    convertGeoPointToLeafletLatLng(
      isGeoPointValid(geoPoint) && geoPoint ? geoPoint : DEFAULT_MAP_CENTER
    )
  )
  const marker = markerRef.current

  useEffect(() => {
    if (geoPoint && geoPoint.latitude && geoPoint.longitude) {
      const newPosition = convertGeoPointToLeafletLatLng(geoPoint)
      setMarkerPosition(newPosition)
    }
  }, [ geoPoint ])

  const handleMarkerDragEnd = useCallback(() => {
    if (marker != null) {
      const markerLatLng = marker.getLatLng()
      setMarkerPosition(markerLatLng)
      onPositionChanged?.(convertLeafletLatLngToGeoPoint(markerLatLng))
    }
  }, [ marker, onPositionChanged ])

  return (
    <MapContainer
      center={markerPosition}
      zoom={13}
      scrollWheelZoom={true}
      style={{
        height: 400,
        width: '100%'
      }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapController geoPoint={geoPoint} />

      {!!markerPosition && (
        <Marker
          icon={MapMarker('blue')}
          position={markerPosition}
          draggable
          eventHandlers={{
            dragend: handleMarkerDragEnd
          }}
          ref={markerRef}
        />
      )}
    </MapContainer>
  )
}
